import React from "react";
import { useNotifications } from "../context/NotificationsContext";
import { useNavigate } from "react-router-dom";
import { Button } from "./UI";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../lib/cn";

const GlobalNotificationsDropdown = () => {
  const {
    isOpen,
    dropdownPosition,
    recentNotifications,
    handleMarkAsRead,
    handleMarkAllAsRead,
    unreadCount,
  } = useNotifications();

  const navigate = useNavigate();

  if (!isOpen) return null;

  return (
    <div
      id="global-notifications-dropdown"
      className="fixed z-[999999] w-80 bg-white dark:bg-slate-800 border border-[var(--color-border)] rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[480px] animate-in fade-in zoom-in duration-200"
      style={{
        left: `${dropdownPosition.left}px`,
        top: `${dropdownPosition.top}px`,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-[var(--color-border-light)] bg-slate-50/50 dark:bg-slate-900/50 backdrop-blur-sm flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-black text-[var(--color-text-primary)]">
            Уведомления
          </h3>
          {unreadCount > 0 && (
            <span className="flex h-2 w-2 rounded-full bg-[var(--color-primary)] animate-pulse"></span>
          )}
        </div>
        <button
          onClick={() => navigate("/notifications")}
          className="text-[10px] font-black text-[var(--color-primary)] uppercase tracking-widest hover:underline"
        >
          Все
        </button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto custom-scrollbar bg-white dark:bg-slate-800">
        <AnimatePresence>
          {recentNotifications.length === 0 ? (
            <div className="py-12 px-6 text-center space-y-2 opacity-50">
              <div className="text-3xl">🔔</div>
              <p className="text-xs font-bold text-[var(--color-text-secondary)]">
                У вас пока нет уведомлений
              </p>
            </div>
          ) : (
            recentNotifications.map((n, i) => (
              <motion.div
                key={n.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className={cn(
                  "px-4 py-3 border-b border-[var(--color-border-light)] last:border-none cursor-pointer group transition-all",
                  !n.is_read
                    ? "bg-blue-50/30 dark:bg-blue-500/5"
                    : "hover:bg-slate-50 dark:hover:bg-slate-900/50",
                )}
                onClick={() => {
                  if (!n.is_read) handleMarkAsRead(n.id);
                  if (n.order_id) navigate(`/orders/details/${n.order_id}`);
                }}
              >
                <div className="flex gap-3">
                  <div className="pt-1">
                    {!n.is_read ? (
                      <div className="w-1.5 h-1.5 rounded-full bg-[var(--color-primary)] shadow-[0_0_8px_rgba(59,130,246,0.6)]"></div>
                    ) : (
                      <div className="w-1.5 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700"></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <h4
                        className={cn(
                          "text-xs font-black truncate",
                          !n.is_read
                            ? "text-[var(--color-text-primary)]"
                            : "text-[var(--color-text-secondary)]",
                        )}
                      >
                        {n.title}
                      </h4>
                      <span className="text-[9px] font-bold text-[var(--color-text-tertiary)] uppercase whitespace-nowrap">
                        {n.created_at_formatted}
                      </span>
                    </div>
                    <p className="text-[11px] font-medium text-[var(--color-text-secondary)] line-clamp-2 leading-tight">
                      {n.message}
                    </p>
                    {n.student_class && (
                      <div className="pt-1">
                        <span className="text-[9px] font-black text-[var(--color-text-tertiary)] uppercase tracking-wider">
                          {n.student_class} • {n.student_name}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-[var(--color-border-light)] bg-slate-50/50 dark:bg-slate-900/50">
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              fullWidth
              onClick={handleMarkAllAsRead}
              className="h-9 px-3 text-[10px] font-bold rounded-xl active:scale-95 transition-all"
            >
              Прочитать все
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            fullWidth
            onClick={() => navigate("/notifications")}
            className="h-9 px-3 text-[10px] font-bold rounded-xl active:scale-95 transition-all shadow-md shadow-blue-500/10"
          >
            Все уведомления
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GlobalNotificationsDropdown;
